import React, { useState, useEffect } from 'react';

import {
  Add
} from "@mui/icons-material";
import { Tooltip, Fab } from "@mui/material";
import Grid from '@mui/material/Grid2';
import { 
  FeatureSource,
  ModelBase,
} from "@models";
import { 
  Feat, 
  UpgradableNumber,
} from "@dndModels";
import { FEAT_TIERS } from "@dndModels/Constants";
import { FeatTier } from "@dndModels/Types";

import API from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";

import SelectStringBox from "@components/input/selects/SelectStringBox";
import CheckBox from "@components/input/CheckBox";
import EditableLabel from "@components/input/strings/EditableLabel";
import UpgradableNumberBox from "@dndComponents/UpgradableNumberBox";

import FeatureSourceInput from "@dndComponents/FeatureSourceInput";

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

interface Props {
  obj?: ModelBase;
}

export default function FeatEdit({ obj: param_obj }: Props) {
  const [reloading, setReloading] = useState(false);
  const [obj, setObj] = useState<Feat>(param_obj instanceof Feat ? param_obj : api.applyWorkingOn(new Feat()));
  const [change_time, setChangeTime] = useState(0);
  
  const disabled = api.user === null || !api.online || (!api.user.admin && obj._id !== "" && obj.owner_id !== api.user._id);
  
  useEffect(() => {
    if(reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  return (
    <FeatureSourceInput 
      obj={obj}
      data_type="feat"
      disabled={disabled}
      change_time={change_time}
      onChange={(changed: FeatureSource) => {
        if (changed instanceof Feat) {
          const new_obj = obj;
          new_obj.copy(changed);
          setObj(new_obj);
        }
      }}>
      <Grid container spacing={0} direction="row">
        <Grid size={6}>
          <SelectStringBox 
            name="Feat Type"
            value={ obj.feat_tier } 
            options={FEAT_TIERS}
            disabled={disabled}
            onChange={(changed: FeatTier) => {
              const new_obj = obj;
              new_obj.feat_tier = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid size={6}>
          <CheckBox 
            name="Repeatable"
            value={ obj.repeatable } 
            disabled={disabled}
            onChange={(changed: boolean) => {
              const new_obj = obj;
              new_obj.repeatable = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid size={12}>
          <span className="header">
            Formulae
          </span>
          { !disabled &&
            <Tooltip title={`Create New Formula`}>
              <Fab size="small" color="primary" style={{marginLeft: "8px"}}
                onClick={ () => {
                  const new_obj = obj;
                  new_obj.formulae["New Formula"] = new UpgradableNumber();
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                  setReloading(true);
                }}>
                <Add/>
              </Fab>
            </Tooltip>
          }
        </Grid>
        { !reloading && Object.keys(obj.formulae).sort((a, b) => { return a.localeCompare(b); }).map(
          (formula_key, key) => {
            return (
              <Grid
                key={key}
                container
                spacing={0}
                direction="row"
                size={12}>
                <Grid size={2}>
                  <EditableLabel 
                    name="Formula Key"
                    value={formula_key}
                    onChange={(changed: string) => {
                      const new_obj = obj;
                      const old_value = new_obj.formulae[formula_key].clone();
                      delete new_obj.formulae[formula_key];
                      new_obj.formulae[changed] = old_value;
                      setObj(new_obj);
                      setChangeTime(common_util.get_time_number());
                      setReloading(true);
                    }}
                  />
                </Grid>
                <Grid size={10}>
                  <UpgradableNumberBox 
                    value={ obj.formulae[formula_key] as UpgradableNumber } 
                    name={ formula_key }
                    disabled={disabled}
                    onChange={(changed: UpgradableNumber) => {
                      const new_obj = obj;
                      new_obj.formulae[formula_key] = changed;
                      setChangeTime(common_util.get_time_number());
                      setObj(new_obj);
                    }}
                  />
                </Grid>
              </Grid>
            );
          }
        )}
      </Grid>
    </FeatureSourceInput>
  ); 
}
